import {KalmanFilter} from "./kalman";

export class KalmanPredictor {
  constructor(predictionDistance) {
    this._predictionDistance = predictionDistance;
    
    this._sigmaProcess = 0.01;
    this._sigmaMeasurement = 1.0;
    this._lastX = 0;
    this._lastY = 0;
    this._reset = true;
  }
  
  reset() {
    this._reset = true;
  }
  
  addPoint({x, y}) {
    if(this._reset) {
      this._xKalman = this._createPenKalmanFilter(x);
      this._yKalman = this._createPenKalmanFilter(y);
      this._reset = false;
    }
    
    this._xKalman.update([x]);
    this._yKalman.update([y]);
    this._lastX = x;
    this._lastY = y;
  }
  
  calculatePrediction() {
    let x = this._lastX;
    let vx = this._xKalman.state().get([1]);
    let ax = this._xKalman.state().get([2]);
    let jx = this._xKalman.state().get([3]);
    
    let y = this._lastY;
    let vy = this._yKalman.state().get([1]);
    let ay = this._yKalman.state().get([2]);
    let jy = this._yKalman.state().get([3]);
    
    let speed = Math.sqrt(vx * vx + vy * vy);
    let speedFactor = this._normalizeRange(speed, 0, 2);
    
    let jank = Math.sqrt(jx*jx + jy*jy);
    let jankFactor = 1 - this._normalizeRange(jank, 0.02, 0.2);
    
    let confidenceFactor = speedFactor * jankFactor;
    
    let prediction_points = this._predictionDistance * confidenceFactor;
    prediction_points = Math.max(prediction_points, 1);
    
    let dt = 1.1;
    let prediction = [];
    for(let i = 0; i < prediction_points; i++) {
      ax += jx*dt*0.1;
      vx += ax*dt*0.7;
      x += vx*dt;
      
      ay += jy*dt*0.1;
      vy += ay*dt*0.7;
      y += vy*dt;
      
      prediction.push({x, y});
    }
    
    return prediction;
  }
  
  _normalizeRange(x, minVal, maxVal) {
    let normalized = (x - minVal) / (maxVal - minVal);
    return Math.min(1.0, Math.max(normalized, 0.0));
  }

  _createPenKalmanFilter(initialLocation) {
    const X_DIM = 4;
    const Z_DIM = 1;
    const dt = 1.0;
    
    // State transition matrix is derived from basic physics:
    // new_x = x + v * dt + 1/2 * a * dt^2 + 1/6 * jank * dt^3
    // new_v = v + a * dt + 1/2 * jank * dt^2
    // ...
    let F = math.matrix([
      [1.0, dt, 0.5*dt*dt, 0.16*dt*dt*dt],
      [0, 1.0, dt, 0.5*dt*dt],
      [0, 0, 1.0, dt],
      [0, 0, 0, 1.0],
    ]);
    
    // We model the system noise as a noisy force on the pen.
    // The matrix G describes the impact of that noise on each state.
    let G = math.matrix([
      [0.16*dt*dt*dt], [0.5*dt*dt], [dt], [1],
    ]);
    let Q = math.multiply(math.multiply(G, math.transpose(G)), this._sigmaProcess);
    
    // Measurements only impact the location
    let H = math.matrix([[1, 0, 0, 0]]);
    
    // Measurement noise is a 1-D normal distribution
    let R = math.matrix([[this._sigmaMeasurement]]);
    
    // Set up Kalman filter with 4 dimensional state
    let kalman = new KalmanFilter({
      A: F,
      C: H,
      Q: Q,
      R: R,
      P: math.identity(X_DIM, X_DIM),
      dt: dt,
    });
    
    let x_hat = [initialLocation, 0, 0, 0];
    kalman.init(0, x_hat);
    
    return kalman;
  }
}